import {biLogger, getOrigin, HP_VERSION} from '../utils/bi-logger';

interface Dimensions {
  windowHeight: number;
  folds: {
    [key: string]: {
      offsetTop: number;
      offsetHeight: number;
    };
  };
}

interface FoldDef {
  foldName: string;
  windowPercentageOccupiedByFold?: number;
}

interface Options {
  timeOnFold?: number;
}

let folds: Array<HTMLElement> = [];
let dimensions: Dimensions;

let foldReportTimeout;
let currentFoldName;
let options: Options;
let startedViewingFold: number;
let reportTimeOnPreviousFold: boolean;

const cancelFoldReport = () => {
  if (foldReportTimeout) {
    clearTimeout(foldReportTimeout);
  }
};

const getFoldByFoldName = (name: string): HTMLElement => {
  return folds.find(fold => fold.getAttribute('data-foldname') === name);
};

const getFoldNum = (fold: HTMLElement): number => {
  return parseInt(fold.getAttribute('data-foldnum'), 10);
};

const reportTimeOnFold = () => {
  const timeOnPreviousFold = Date.now() - startedViewingFold;
  biLogger.getLogger('hp').timeInFold({
    fold_name: currentFoldName,
    fold: getFoldNum(getFoldByFoldName(currentFoldName)),
    delta: timeOnPreviousFold,
    total: folds.length,
    origin: getOrigin(),
    version: HP_VERSION
  });
  reportTimeOnPreviousFold = false;
};

const handleFoldChange = (fold: FoldDef) => {
  if (!fold) {
    if (reportTimeOnPreviousFold) {
      reportTimeOnFold();
      reportTimeOnPreviousFold = false;
    }
    currentFoldName = '';
    cancelFoldReport();
    return;
  }

  if (currentFoldName === fold.foldName) {
    return;
  }

  if (reportTimeOnPreviousFold) {
    reportTimeOnFold();
    reportTimeOnPreviousFold = false;
  }

  currentFoldName = fold.foldName;
  startedViewingFold = Date.now();

  cancelFoldReport();

  foldReportTimeout = setTimeout(() => {
    reportTimeOnPreviousFold = true;
    biLogger.getLogger('hp').hpUserInFold({
      fold_name: currentFoldName,
      fold: getFoldNum(getFoldByFoldName(currentFoldName)),
      total: folds.length,
      origin: getOrigin(),
      version: HP_VERSION
    });
  }, options.timeOnFold);
};

const calculateDimensions = () => {
  dimensions = {
    windowHeight: window.innerHeight,
    folds: {}
  };
  folds.forEach(fold => {
    const foldName = getFoldName(fold);
    const style = window.getComputedStyle(fold);
    const marginTop = parseInt(style.marginTop, 10);
    const marginBottom = parseInt(style.marginBottom, 10);
    dimensions.folds[foldName] = {
      offsetTop: fold.offsetTop  - marginTop,
      offsetHeight: fold.offsetHeight + marginTop + marginBottom
    };
  });
};

const handleScroll = () => {
  const viewableFolds: Array<ViewableFold> = [];
  const {scrollY} = window;

  folds.forEach(fold => {
    const foldName = getFoldName(fold);
    const foldDims = dimensions.folds[foldName];

    // Calculate the % of the screen occupied by this fold
    const foldYBottom = foldDims.offsetTop + foldDims.offsetHeight;
    const windowYBottom = scrollY + dimensions.windowHeight;
    const foldStart = scrollY > foldDims.offsetTop ? scrollY : foldDims.offsetTop;
    const foldEnd = windowYBottom < foldYBottom ? windowYBottom : foldYBottom;
    const foldHeight = foldEnd - foldStart;
    const windowPercentageOccupiedByFold = foldHeight > 0 ? Math.round(foldHeight / dimensions.windowHeight * 100) : 0;

    if (windowPercentageOccupiedByFold) {
      const foldHeightInWindow = foldDims.offsetHeight - (foldStart - foldDims.offsetTop) - (foldYBottom - foldEnd);
      const percentageOfFoldInWindow = Math.round(foldHeightInWindow / foldDims.offsetHeight * 100);
      viewableFolds.push({foldName, windowPercentageOccupiedByFold, percentageOfFoldInWindow});
    }
  });

  subscribers.forEach(callback => callback(viewableFolds));

  const currentFold = viewableFolds.find(fold => {
    const percentage = fold.windowPercentageOccupiedByFold;
    return percentage > 50 || (percentage === 50 && fold.foldName === currentFoldName);
  });

  handleFoldChange(currentFold);
};

const getFoldName = (fold: HTMLElement) => {
  return fold.getAttribute('data-foldname');
};

interface InitArgs {
  timeOnFold: number;
  folds: NodeList;
}

export interface ViewableFold {
  foldName: string;
  windowPercentageOccupiedByFold: number;
  percentageOfFoldInWindow: number;
}

export const startTrackingFolds = (args: InitArgs) => {
  if (!args.folds.length) {
    return;
  }
  options = {
    timeOnFold: args.timeOnFold || 3000
  };
  folds = Array.from<Node>(args.folds).map((node: HTMLElement) => node);
  window.addEventListener('scroll', handleScroll);
  window.addEventListener('resize', calculateDimensions);
  calculateDimensions();
  handleScroll();
};

export const stopTrackingFolds = () => {
  cancelFoldReport();
  window.removeEventListener('scroll', handleScroll);
  window.removeEventListener('resize', calculateDimensions);
  options = null;
  folds = [];
  dimensions = null;
  subscribers = [];
};

type SubscriberCallback = (folds: Array<ViewableFold>) => void;

let subscribers: Array<SubscriberCallback> = [];

export const subscribe = (callback: SubscriberCallback) => {
  subscribers.push(callback);
};
